import { useEffect, useState } from 'react';

import { fetchCapabilities, featureCapability } from '../capabilities';

/**
 * Boot hook — fetches tenant entitlements once per mount and exposes them.
 * Shared across all client ext-* packages so every extension self-gates off the
 * same single boot call (TASK157 D3 / subtask 2.4).
 *
 * Lives in ext-core (not ext-search) so a future ext-zoom / ext-badges can
 * consume it without depending on ext-search.
 *
 * Fail-soft: if the capabilities fetch fails or the host proxy isn't mounted yet,
 * `capabilities` resolves to `{}` — every extension then reads its flag as
 * disabled and renders nothing, preserving the "silent failure" contract.
 *
 * @param {{ rootUrl?: string }} [opts]
 * @returns {{ capabilities: Record<string, any>, ready: boolean, feature: (name: string) => { enabled: boolean } }}
 */
export const useCapabilities = ({ rootUrl } = {}) => {
  const [capabilities, setCapabilities] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let isActive = true;

    fetchCapabilities({ rootUrl })
      .then(caps => {
        if (isActive) {
          setCapabilities(caps || {});
          setReady(true);
        }
      })
      .catch(() => {
        if (isActive) {
          setCapabilities({});
          setReady(true);
        }
      });

    return () => {
      isActive = false;
    };
  }, [rootUrl]);

  const feature = name => featureCapability(capabilities, name);

  return { capabilities, ready, feature };
};
